import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DashboardMainContainer from "../../../components/common/Containers/DashboardMainContainer";
import {
  AdminPageTitle,
  AdminMetadataText,
  AdminStatusBadge,
  AdminHeaderAction,
  AdminAlertText,
  AdminMainText,
  AdminChannelBadge,
  AdminDetailFieldLabel,
  adminWizardTextareaClasses,
} from "../../../sections/AdminDashboard/shared/adminUx";
import { runTextPipeline, validateFile } from "../../../utils/contentSafety";
import Alerts from "../../../components/common/Alerts/Alerts";
import { BUSINESS_INBOX_CONTENT_CLASS, BUSINESS_LIST_PAGE_TITLE_CLASS, BUSINESS_DETAIL_MAIN_HEADER_CLASS } from "../../../sections/BusinessDashboard/shared/businessLayoutSpec";

const BusinessTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketFromState = location.state?.ticket;

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [replyFile, setReplyFile] = useState(null);

  const formatDate = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return (
      d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) +
      ", " +
      d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const ticketStatusKey = (status) => (status === "Resolved" ? "Approved" : status);

  const priorityStatusKey = (priority) => {
    if (priority === "High") return "Escalated";
    if (priority === "Medium") return "Warning";
    if (priority === "Low") return "Active";
    return priority;
  };

  const formatChannelDisplay = (channel) => {
    if (!channel) return "Web";
    let s = String(channel).trim().replace(/\s+form$/i, "").trim();
    return s || "Web";
  };

  const isSupportSender = (sender) => sender === "Support" || sender === "Support Team";

  const buildTicketWithMessages = (base) => {
    const now = new Date();
    const initialMessage =
      base.subject === "Charged twice for subscription"
        ? "We were charged twice today for our Pro Annual subscription. The first charge went through at 8:42 AM and the second at 8:44 AM — we only intended to renew once. Could you please investigate and process a refund for the duplicate charge? We've attached our invoice and the payment confirmation from our bank."
        : base.description || "Initial message.";
    const messages = [
      {
        id: "msg1",
        sender: "Business",
        senderName: base.businessName || "You",
        channel: "Web form",
        time: "09:31",
        timestamp: new Date(now.getTime() - 3 * 60 * 60 * 1000),
        message: initialMessage,
      },
      {
        id: "msg2",
        sender: "Support Team",
        senderName: "Support Team",
        channel: "Email",
        time: "09:45",
        timestamp: new Date(now.getTime() - 2.75 * 60 * 60 * 1000),
        message:
          "Thank you for contacting Tradesposter Support. We're sorry to hear about the duplicate charge and we take billing accuracy very seriously. We've received your ticket and our billing team is looking into this. We'll need to verify the two transactions on our side and confirm the duplicate with our payment provider. We aim to get back to you within 1–2 business days with an update and next steps for the refund. In the meantime, if you have any further questions or additional documentation (such as bank statements or screenshots) that could help speed up our investigation, please feel free to reply to this ticket and attach them. We appreciate your patience.",
      },
      {
        id: "msg3",
        sender: "Business",
        senderName: base.businessName || "You",
        channel: "Web form",
        time: "10:15",
        timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000),
        message:
          "Thanks for the quick response. I've attached the invoice (invoice_4481.pdf) and a screenshot of the payment confirmation from our bank showing both charges on the same day. Both transactions show up as coming from Tradesposter — the first at 8:42 AM for $599 and the second at 8:44 AM for the same amount. Our account email is the one we used to submit this ticket, and our business name is on both transactions. Please let us know if you need any other details or if there's anything else we can provide to help with the investigation.",
        attachments: [
          { id: "a1", name: "invoice_4481.pdf", type: "PDF", size: "245 KB" },
          { id: "a2", name: "payment_confirmation.png", type: "PNG", size: "189 KB" },
        ],
      },
      {
        id: "msg4",
        sender: "Support Team",
        senderName: "Support Team",
        channel: "Email",
        time: "11:02",
        timestamp: new Date(now.getTime() - 1.5 * 60 * 60 * 1000),
        message:
          "We've confirmed the duplicate charge with our payment provider and apologise for the inconvenience. Our billing team has verified both transactions and identified that the second charge was triggered in error during the renewal process. A refund for the second transaction ($599) has been approved and is now being processed through our payment provider. Refunds typically appear in your account within 5–7 business days, depending on your bank's processing times. You'll receive an email confirmation once the refund has been sent to your card issuer. We've also added a note to your account so our team can avoid any similar issues in future, and we've flagged the renewal process for review. If you don't see the refund after 7 business days or if you have any questions, please reply to this ticket and we'll follow up promptly.",
      },
      {
        id: "msg5",
        sender: "Support Team",
        senderName: "Support Team",
        channel: "Email",
        time: "11:08",
        timestamp: new Date(now.getTime() - 1.45 * 60 * 60 * 1000),
        message:
          "One more thing — we've applied a $25 credit to your account as an apology for the inconvenience. It will show on your next invoice and will be automatically applied to your next subscription renewal. We truly value your business and appreciate you taking the time to bring this to our attention. Your feedback helps us improve our processes and prevent similar issues for other customers. Thank you for your patience and for being a valued member of the Tradesposter community. If there's anything else we can help with, please don't hesitate to reach out.",
      },
    ];
    return {
      ...base,
      description: base.description || initialMessage,
      attachments: base.attachments || [
        { id: "att1", name: "invoice_4481.pdf", type: "PDF", url: "#" },
        { id: "att2", name: "stripe_charge.png", type: "Image", url: "#" },
      ],
      messages,
    };
  };

  useEffect(() => {
    const fetchTicket = async () => {
      setLoading(true);
      try {
        if (ticketFromState) {
          const enhanced = buildTicketWithMessages(ticketFromState);
          setTicket(enhanced);
        } else {
          await new Promise((r) => setTimeout(r, 400));
          const dummy = buildTicketWithMessages({
            ticketId: ticketId || "TCK-19281",
            subject: "Charged twice for subscription",
            priority: "High",
            category: "Billing & Subscription",
            status: "Open",
            createdDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000),
            businessName: "Your Business",
          });
          setTicket(dummy);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketId, ticketFromState]);

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    if (ticket?.status === "Closed") return;
    const result = runTextPipeline(replyText.trim(), "support");
    if (!result.valid) {
      Alerts.error(result.message);
      return;
    }
    const now = new Date();
    const newMsg = {
      id: `msg-${Date.now()}`,
      sender: "Business",
      senderName: "You",
      channel: "Web form",
      time: now.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" }),
      timestamp: now,
      message: result.normalized ?? replyText,
      attachments: replyFile ? [{ id: "new1", name: replyFile.name, type: "File", size: "" }] : undefined,
    };
    setTicket((prev) => {
      const shouldReopen =
        prev.status === "Awaiting Reply" || prev.status === "Resolved";
      return {
        ...prev,
        messages: [...(prev.messages || []), newMsg],
        lastUpdated: now,
        ...(shouldReopen ? { status: "In Progress" } : {}),
      };
    });
    setReplyText("");
    setReplyFile(null);
  };

  const canReplyToTicket = ticket?.status !== "Closed";

  const handleReplyFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setReplyFile(null);
      return;
    }
    const isImage = (file.type || "").startsWith("image/");
    const result = validateFile(file, isImage ? "image" : "document");
    if (!result.valid) {
      Alerts.error(result.message);
      e.target.value = "";
      return;
    }
    setReplyFile(file);
    e.target.value = "";
  };

  const threadMessages = useMemo(() => {
    if (!ticket?.messages) return [];
    const sorted = [...ticket.messages].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });
    if (!ticket.description) return sorted;
    let skippedInitialBusiness = false;
    return sorted.filter((m) => {
      if (isSupportSender(m.sender)) return true;
      if (!skippedInitialBusiness) {
        skippedInitialBusiness = true;
        return false;
      }
      return true;
    });
  }, [ticket?.messages, ticket?.description]);

  const renderMessageBubble = (message, msgIdx = 0) => {
    if (!ticket) return null;
    const isSupport = isSupportSender(message.sender);
    const senderDisplay = isSupport
      ? "Support Team"
      : message.senderName || message.sender || "You";
    const messageTimestamp = message.timestamp
      ? formatDateTime(message.timestamp)
      : message.time || "—";
    const channelBadge = formatChannelDisplay(message.channel || ticket.channel || "Web form");
    const avatarSrc =
      ticket.business?.logo || `https://i.pravatar.cc/100?img=${(msgIdx % 70) + 1}`;

    return (
      <div
        key={message.id || `msg-${msgIdx}`}
        className={`flex ${isSupport ? "justify-end" : "justify-start"}`}
      >
        <div
          className={`min-w-[320px] max-w-[85%] rounded-2xl px-5 py-4 ${
            isSupport
              ? "bg-primaryblue text-white rounded-br-md"
              : "bg-mistygray text-offblack rounded-bl-md"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {isSupport ? (
                <div className="w-8 h-8 rounded-full flex-shrink-0 bg-white/35 flex items-center justify-center ring-1 ring-white/50">
                  <i className="fi fi-rs-headphones text-white text-sm" aria-hidden />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full flex-shrink-0 overflow-hidden bg-white border-2 border-white shadow-sm ring-1 ring-offblack/10">
                  <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <AdminMainText className={`text-sm ${isSupport ? "text-white" : "text-offblack"}`}>
                {senderDisplay}
              </AdminMainText>
              <AdminChannelBadge label={channelBadge} onSupport={isSupport} />
            </div>
            <AdminMetadataText
              className={`whitespace-nowrap ${isSupport ? "text-white/80" : "text-mediumgray"}`}
            >
              {messageTimestamp}
            </AdminMetadataText>
          </div>
          <AdminMainText
            className={`text-sm leading-relaxed whitespace-pre-wrap block ${
              isSupport ? "text-white" : "text-offblack"
            }`}
          >
            {message.message}
          </AdminMainText>
          {message.attachments && message.attachments.length > 0 && (
            <div className={`mt-4 rounded-xl p-4 ${isSupport ? "bg-white/15" : "bg-lightgray/80"}`}>
              <AdminMetadataText
                className={`block mb-2 ${isSupport ? "text-white/90" : "text-offblack"}`}
              >
                Attachments
              </AdminMetadataText>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((att) => (
                  <a
                    key={att.id || att.name}
                    href={att.url || "#"}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 flex-shrink-0 max-w-full ${
                      isSupport
                        ? "bg-white/20 hover:bg-white/30"
                        : "bg-white border border-lightgray hover:bg-mistygray"
                    } transition-colors`}
                  >
                    <i
                      className={`fi fi-rs-document flex-shrink-0 text-sm ${
                        isSupport ? "text-white" : "text-primaryblue"
                      }`}
                      aria-hidden
                    />
                    <AdminMainText
                      className={`text-sm truncate min-w-0 ${isSupport ? "text-white" : "text-offblack"}`}
                    >
                      {att.name}
                    </AdminMainText>
                    {att.type ? (
                      <AdminMetadataText
                        className={`flex-shrink-0 ${isSupport ? "text-white/80" : "text-mediumgray"}`}
                      >
                        · {att.type}
                        {att.size ? ` · ${att.size}` : ""}
                      </AdminMetadataText>
                    ) : null}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
        <div className="flex min-h-[40vh] items-center justify-center">
          <AdminMetadataText className="text-offblack">Loading ticket...</AdminMetadataText>
        </div>
      </DashboardMainContainer>
    );
  }

  if (!ticket) {
    return (
      <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
        <AdminAlertText className="text-alertred mb-4">Ticket not found.</AdminAlertText>
        <AdminHeaderAction onClick={() => navigate("/business-dashboard/tickets/open")}>
          Back to tickets
        </AdminHeaderAction>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className="lg:px-5 lg:pb-10 !bg-white">
      <div className={`${BUSINESS_INBOX_CONTENT_CLASS} space-y-6`}>
        {/* Minimal Header – same as Admin TicketDetail */}
        <DashboardMainContainer className={BUSINESS_DETAIL_MAIN_HEADER_CLASS}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <AdminPageTitle className={BUSINESS_LIST_PAGE_TITLE_CLASS}>{ticket.subject}</AdminPageTitle>
              <div className="flex items-center gap-3 flex-wrap">
                <AdminMetadataText className="text-offblack">
                  Created {formatDate(ticket.createdDate)}
                </AdminMetadataText>
                <AdminStatusBadge
                  status={ticketStatusKey(ticket.status)}
                  label={ticket.status || "—"}
                />
                <AdminStatusBadge
                  status={priorityStatusKey(ticket.priority)}
                  label={`${ticket.priority || "—"} priority`}
                />
              </div>
            </div>
          </div>
        </DashboardMainContainer>

        {/* Conversation thread — aligned with admin TicketDetail bubbles */}
        <DashboardMainContainer className="!bg-white">
          <div className="mt-6 lg:px-4 space-y-8">
            {ticket.description &&
              renderMessageBubble(
                {
                  id: "initial-request",
                  sender: "Business",
                  senderName: ticket.businessName || "You",
                  channel: ticket.channel || "Web form",
                  timestamp: ticket.createdDate,
                  message: ticket.description,
                  attachments: ticket.attachments,
                },
                0
              )}

            {threadMessages.map((message, msgIdx) => renderMessageBubble(message, msgIdx + 1))}

            {canReplyToTicket && (
              <div className="rounded-xl border border-lightgray bg-white p-5 mt-4">
                <AdminDetailFieldLabel className="!text-sm !mb-3 block">
                  Reply to ticket
                </AdminDetailFieldLabel>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here..."
                      className={`${adminWizardTextareaClasses(false)} min-h-[120px]`}
                      style={{ resize: "vertical" }}
                      rows={5}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-lightgray bg-mistygray px-3 py-2 text-sm text-offblack hover:border-primaryblue hover:bg-pastalblue transition-colors">
                        <i className="fi fi-rs-paperclip text-mediumgray" />
                        <span>Attach file</span>
                        <input
                          type="file"
                          onChange={handleReplyFileChange}
                          className="hidden"
                        />
                      </label>
                      <AdminHeaderAction
                        onClick={handleSendReply}
                        disabled={!replyText.trim()}
                      >
                        Send reply
                      </AdminHeaderAction>
                      {replyFile && (
                        <AdminMetadataText className="text-mediumgray flex items-center gap-1">
                          <i className="fi fi-rs-check-circle text-freshgreen" />
                          {replyFile.name}
                        </AdminMetadataText>
                      )}
                    </div>
                  </div>
                )}

            {ticket.status === "Resolved" && canReplyToTicket && (
              <div className="rounded-xl border border-lightgray bg-white p-5 mt-4">
                <AdminDetailFieldLabel className="!text-sm !mb-2 block">
                  Ticket resolved
                </AdminDetailFieldLabel>
                <AdminMetadataText className="text-mediumgray block">
                  If you still need help, send a reply below before the reopening period ends to reopen this ticket.
                </AdminMetadataText>
              </div>
            )}
          </div>
        </DashboardMainContainer>
      </div>
    </DashboardMainContainer>
  );
};

export default BusinessTicketDetail;
